import type { FC } from "react";
import { useEffect, useMemo } from "react";

import type { GroupedPurposesBySection } from "@ui/components/ConsentSection";
import { Controller, useFormContext } from "@ui/components/Form";
import { MarkdownWrapper } from "@ui/components/MarkdownWrapper";
import { useTranslations } from "@ui/providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { RadioButton, SupportingText } from "@jti/core-components";

import styles from "./SingleOptionalConsent.module.scss";

const { sanitize } = dompurify;

type SelectedValue = "true" | "false";

type ConsentOption = {
  value: SelectedValue;
  label: string;
};

export type SingleOptionalConsentProps = {
  purposes?: GroupedPurposesBySection[];
  defaultValue?: SelectedValue;
  onAcceptAllClick?: () => void;
  onDeclineAllClick?: () => void;
  disabled?: boolean;
  required?: boolean;
};

const SINGLE_OPTION_FIELD_NAME = "singleConsentOption";

export const SingleOptionalConsent: FC<SingleOptionalConsentProps> = ({
  purposes,
  disabled,
  required = true,
  defaultValue,
  onAcceptAllClick,
  onDeclineAllClick,
}) => {
  const config = useConfig();
  const { __ } = useTranslations();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors?.[SINGLE_OPTION_FIELD_NAME]?.message;

  const CONSENT_OPTIONS: Array<ConsentOption> = useMemo(
    () => [
      { value: "true", label: __("auth.registration.consents.yes") },
      { value: "false", label: __("auth.registration.consents.no") },
    ],
    [__]
  );

  const consentDescription = useMemo(() => {
    return purposes?.[0]?.purposeDescription || __("auth.registration.consents.optional.description");
  }, [purposes, __]);

  const purposeDescriptionWithLinks = config.ONE_TRUST_DYNAMIC_LINKS
    ? sanitize(replaceOneTrustPlaceholdersWithLinks(consentDescription, config.ONE_TRUST_DYNAMIC_LINKS), {
        ADD_ATTR: ["target"],
      })
    : consentDescription;

  const handleValueChange = (newValue: SelectedValue) => {
    if (newValue === "true") {
      onAcceptAllClick?.();
    } else if (newValue === "false") {
      onDeclineAllClick?.();
    }
  };

  useEffect(() => {
    if (defaultValue) handleValueChange(defaultValue);
  }, [defaultValue]);

  return (
    <div className={styles.wrapper}>
      <MarkdownWrapper>{purposeDescriptionWithLinks}</MarkdownWrapper>
      <Controller
        name={SINGLE_OPTION_FIELD_NAME}
        rules={{
          required: {
            value: required,
            message: __("form_validation.field_required"),
          },
        }}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <>
            {CONSENT_OPTIONS.map(({ label, value }) => (
              <RadioButton
                key={value}
                name={label}
                value={value}
                label={label}
                disabled={disabled}
                checked={field.value === value}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  handleValueChange(e.target.value as SelectedValue);
                }}
              />
            ))}
          </>
        )}
      />
      {errorMessage ? <SupportingText variant="error">{String(errorMessage)}</SupportingText> : null}
    </div>
  );
};
